import {
  DownloadOutlined,
  ExportOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Input,
  InputNumber,
  Popover,
  Select,
  Space,
  Table,
  TableColumnType,
  Typography,
  Upload,
} from "antd";
import { debounce } from "lodash";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";

import ModalPreviewTransfer from "../../components/modal/Warehouse/ModalPreviewTransfer";
import {
  IPartNumberDtoResponse,
  IPartNumberDtoTransfer,
  IWarehouseLocation,
  IWarehouseLocationDetail,
} from "../../models/Warehouse";
import {
  useImportWarehouseTransfer,
  useWarehouses,
} from "../../query/WarehouseQuery";
import { RootState } from "../../store";
import { renderQCStatusTag } from "../../utils/qcHelper";
import { notify } from "../../utils/toast";
const { Text } = Typography;
const WarehouseTransfer = () => {
  const locations = useSelector(
    (state: RootState) => state.warehouse.locations,
  ) as IWarehouseLocation[];
  const [searchParams, setSearchParams] = useState<{
    awb?: string;
    partNumber?: string;
  }>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>();
  const [warehouseId, setWarehouseId] = useState<number | null>(null);
  const [locationId, setLocationId] = useState<number | null>(null);

  const { data: warehouseData, isLoading } = useWarehouses({
    page: currentPage,
    limit: pageSize,
    ...searchParams,
    code: warehouseFilter,
  });
  const filterOptions = locations
    .filter((item) => item.isActive && item.warehouseCode !== "QC")
    .map((item) => ({
      label: item.warehouseCode,
      value: item.warehouseCode,
    }));

  const [selectedItems, setSelectedItems] = useState<IPartNumberDtoTransfer[]>(
    [],
  );
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const importMutation = useImportWarehouseTransfer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchChange = useCallback(
    debounce((field: string, value: string) => {
      setSearchParams((prev) => ({
        ...prev,
        [field]: value || undefined,
      }));
      setCurrentPage(1);
    }, 300),
    [],
  );
  const handleExcelUpload = async (file: File) => {
    await importMutation.mutateAsync(file, {
      onSuccess: (data) => {
        if (data && Array.isArray(data)) {
          const processedItems = data.map((item) => ({
            ...item,
          }));

          setSelectedItems(processedItems);
          setPreviewModalOpen(true);
          notify.success(
            `Đã xử lý ${processedItems.length} mã hàng từ file Excel`,
          );
        } else {
          notify.error("Định dạng dữ liệu không hợp lệ");
        }
      },
      onError: (error: any) => {
        if (error.isAxiosError) {
          const serverMessage = error.response?.data?.message;
          notify.error(
            serverMessage || "Có lỗi khi xử lý file. Vui lòng thử lại.",
          );
        } else {
          notify.error("Kiểm tra lại thông tin dữ liệu xuất kho và thử lại");
        }
      },
    });
    return false;
  };
  const handlePreview = () => {
    if (selectedItems.length === 0) {
      notify.warning("Vui lòng chọn ít nhất một mã hàng");
      return;
    }
    if (!warehouseId || !locationId) {
      notify.warning("Vui lòng chọn kho và vị trí nhận");
      return;
    }

    const invalidItems = selectedItems.filter(
      (it) =>
        !it.exportQuantity ||
        it.exportQuantity <= 0 ||
        !it.reportSAQuantity ||
        it.reportSAQuantity <= 0,
    );

    if (invalidItems.length > 0) {
      notify.error(
        "Vui lòng nhập đầy đủ số lượng SA báo và SL xuất cho tất cả các mã hàng",
      );
      return;
    }

    const overStockItems = selectedItems.filter(
      (it) => it.exportQuantity > it.stock,
    );

    if (overStockItems.length > 0) {
      notify.error("Số lượng xuất không được vượt quá tồn kho");
      return;
    }

    setPreviewModalOpen(true);
  };

  const handlePreviewOk = () => {
    setPreviewModalOpen(false);
    setSelectedItems([]);
  };

  const handlePreviewCancel = () => {
    setPreviewModalOpen(false);
  };

  const columns: TableColumnType<IPartNumberDtoResponse>[] = [
    // {
    //   title: "Ngày",
    //   dataIndex: "importDate",
    //   key: "date",
    //   width: 100,
    //   minWidth: 100,
    //   align: "center",
    // },
    {
      title: "AWB",
      dataIndex: ["awb"],
      key: "awb",
      width: 110,
      minWidth: 70,
      align: "center",
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm kiếm AWB"
              onChange={(e) => {
                handleSearchChange("awb", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: searchParams.awb ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "Mã kho",
      dataIndex: ["location", "warehouse", "warehouseCode"],
      key: "warehouseCode",
      width: 70,
      minWidth: 70,
      align: "center",
    },
    {
      title: "Dự án",
      dataIndex: "project",
      key: "project",
      width: 80,
      minWidth: 80,
      align: "center",
    },
    {
      title: "Mã hàng",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 120,
      minWidth: 120,
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm kiếm mã hàng"
              onChange={(e) => {
                handleSearchChange("partNumber", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: searchParams.partNumber ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "Mã dự án",
      dataIndex: "projectCode",
      key: "projectCode",
      width: 110,
      minWidth: 110,
    },
    {
      title: "QC Status",
      dataIndex: "qcStatus",
      key: "qcStatus",
      width: 120,
      minWidth: 120,
      render: (status: string) => renderQCStatusTag(status),
    },
    {
      title: "Số lượng",
      dataIndex: "stock",
      key: "stock",
      width: 100,
      minWidth: 100,
    },
    {
      title: "Đơn vị tính",
      dataIndex: "unit",
      key: "unit",
      width: 100,
      minWidth: 100,
    },
    {
      title: "Location",
      dataIndex: ["location", "code"],
      key: "location",
      width: 100,
      minWidth: 100,
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      minWidth: 100,
    },
  ];

  const handleAddToExport = (record: any) => {
    if (
      selectedItems.length > 0 &&
      record.project !== selectedItems[0].project
    ) {
      notify.error("Chỉ có thể chọn các mã hàng cùng dự án.");
      return;
    }

    if (!selectedItems.find((item) => item.id === record.id)) {
      setSelectedItems([
        ...selectedItems,
        { ...record, exportQuantity: 1, reportSAQuantity: 1 },
      ]);
    }
  };
  const handleRemoveFromExport = (recordId: number) => {
    setSelectedItems(selectedItems.filter((item) => item.id !== recordId));
  };
  const handleQuantityChange = (recordId: number, quantity: number) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.id === recordId
          ? { ...item, exportQuantity: quantity || 0 }
          : item,
      ),
    );
  };
  const handleQuantitySAChange = (recordId: number, quantity: number) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.id === recordId
          ? { ...item, reportSAQuantity: quantity || 0 }
          : item,
      ),
    );
  };

  const updatedColumns = columns.map((col) => {
    if (col.key === "action") {
      return {
        ...col,
        render: (_: any, record: any) => (
          <Button
            icon={<ExportOutlined />}
            onClick={() => handleAddToExport(record)}
            disabled={
              record.qcStatus === "PENDING" ||
              record.qcStatus === "REJECT" ||
              record.stock <= 0 ||
              selectedItems.some((item) => item.id === record.id) ||
              (selectedItems.length > 0 &&
                record.project !== selectedItems[0].project)
            }
          >
            Chọn
          </Button>
        ),
      };
    }
    return col;
  });

  return (
    <div style={{ maxHeight: "100vh" }}>
      <div className="flex gap-4 flex-wrap lg:flex-nowrap">
        <div className="flex-1 w-full min-w-[600px]">
          <Card size="small" className="mb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-wrap">
              <Space>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    const headers = [
                      "Project",
                      "Mã dự án",
                      "Part Number",
                      "AWB",
                      "Mã kho",
                      "Location",
                      "Số lượng SA",
                      "Số lượng xuất",
                    ];
                    const exampleRow = {
                      Project: "Dự án A",
                      "Mã dự án": "Example: PJ001",
                      "Part Number": "Example: ABC123",
                      AWB: "Example: AWB123456",
                      "Mã kho": "Example: TK2",
                      Location: "Example: TK2-01",
                      "Số lượng SA": "Example: 10",
                      "Số lượng xuất": "Example: 10",
                    };
                    const ws = XLSX.utils.json_to_sheet([exampleRow], {
                      header: headers,
                    });
                    ws["!cols"] = headers.map(() => ({ wch: 20 }));
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(
                      wb,
                      ws,
                      "Điều chuyển kho xuất",
                    );
                    XLSX.writeFile(wb, "dieu_chuyen_kho_xuat_template.xlsx");
                  }}
                >
                  Tải mẫu Excel
                </Button>
                <Upload
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={handleExcelUpload}
                  disabled={
                    importMutation.isPending || !warehouseId || !locationId
                  }
                >
                  <Button
                    loading={importMutation.isPending}
                    icon={<UploadOutlined />}
                    disabled={
                      importMutation.isPending || !warehouseId || !locationId
                    }
                  >
                    Nhập từ Excel
                  </Button>
                </Upload>
              </Space>
              <Popover
                title="Lọc theo mã kho"
                trigger="click"
                content={
                  <div style={{ padding: "8px" }}>
                    <Space direction="vertical">
                      <Space wrap>
                        {filterOptions.map((option) => (
                          <Button
                            key={option.value}
                            type={
                              warehouseFilter === option.value
                                ? "primary"
                                : "default"
                            }
                            onClick={() => {
                              setWarehouseFilter(
                                warehouseFilter === option.value
                                  ? undefined
                                  : option.value,
                              );
                              setCurrentPage(1);
                            }}
                            size="small"
                          >
                            {option.label}
                          </Button>
                        ))}
                      </Space>
                      {warehouseFilter && (
                        <Button
                          type="link"
                          danger
                          size="small"
                          onClick={() => {
                            setWarehouseFilter(undefined);
                            setCurrentPage(1);
                          }}
                        >
                          Xóa bộ lọc
                        </Button>
                      )}
                    </Space>
                  </div>
                }
              >
                <Button type={warehouseFilter ? "primary" : "default"}>
                  Mã kho {warehouseFilter ? `(${warehouseFilter})` : ""}
                </Button>
              </Popover>
            </div>
          </Card>

          <Table
            columns={updatedColumns}
            dataSource={warehouseData?.data || []}
            loading={isLoading}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: warehouseData?.pagination?.total || 0,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50],
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            scroll={{ y: 600 }}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              overflow: "hidden",
              width: "100%",
            }}
          />
        </div>

        <Card
          title={
            <>
              <Space>
                <Text>Điều chuyển sang kho xuất</Text>
                <Select
                  placeholder="Mã kho"
                  size="small"
                  onChange={(_, option: any) => {
                    setWarehouseId(option.key);
                  }}
                  notFoundContent={<Text>Chưa tạo kho xuất</Text>}
                  className="w-36"
                >
                  {locations
                    .filter(
                      (loc: IWarehouseLocation) =>
                        loc.isActive && loc.warehouseCode === "EXPORT",
                    )
                    .map((loc: IWarehouseLocation) => (
                      <Select.Option key={loc.id} value={loc.warehouseCode}>
                        {loc.warehouseCode}
                      </Select.Option>
                    ))}
                </Select>
                <Select
                  placeholder={warehouseId ? "Vị trí" : "Chọn mã kho trước"}
                  size="small"
                  disabled={!warehouseId}
                  onChange={(_, option: any) => setLocationId(option.key)}
                  notFoundContent={<Text>Chưa tạo vị trí</Text>}
                  className="w-36"
                >
                  {locations
                    .find((wh) => wh.id === Number(warehouseId))
                    ?.warehouseLocations?.filter((loc) => loc.isActive)
                    .map((loc: IWarehouseLocationDetail) => (
                      <Select.Option key={loc.id} value={loc.id}>
                        {loc.code}
                      </Select.Option>
                    ))}
                </Select>
              </Space>
            </>
          }
          className="w-full w-xl lg:max-w-3xl"
          extra={
            <Button
              type="primary"
              disabled={
                selectedItems.length === 0 || !warehouseId || !locationId
              }
              onClick={handlePreview}
            >
              <ExportOutlined />
              Preview
            </Button>
          }
        >
          <div className="max-h-[600px] overflow-y-auto">
            {selectedItems.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                Chưa có mã hàng nào được chọn
              </div>
            ) : (
              <Table
                dataSource={selectedItems}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ x: 1200 }}
                columns={[
                  {
                    title: "Part Number",
                    dataIndex: "partNumber",
                    key: "partNumber",
                    width: 5,
                    fixed: "left",
                    render: (text) => (
                      <span className="font-medium">{text}</span>
                    ),
                  },
                  {
                    title: "AWB",
                    dataIndex: "awb",
                    key: "awb",
                    width: 3,
                  },
                  {
                    title: "Dự án",
                    dataIndex: "project",
                    key: "project",
                    width: 2,
                  },
                  {
                    title: "Mã dự án",
                    dataIndex: "projectCode",
                    key: "projectCode",
                    width: 3,
                  },

                  {
                    title: "Tồn kho",
                    dataIndex: "stock",
                    key: "stock",
                    width: 3,
                    align: "center",
                    render: (text) => (
                      <span className="font-semibold text-blue-600">
                        {text}
                      </span>
                    ),
                  },
                  {
                    title: "SA báo",
                    key: "reporteSAQuantity",
                    width: 3,
                    render: (_, record) => (
                      <InputNumber
                        min={0}
                        value={record.reportSAQuantity}
                        onChange={(value) =>
                          handleQuantitySAChange(record.id, value || 0)
                        }
                        formatter={(value) =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }
                        size="small"
                        required
                      />
                    ),
                  },
                  {
                    title: "SL xuất",
                    key: "exportQuantity",
                    width: 3,
                    render: (_, record) => (
                      <InputNumber
                        min={0}
                        max={record.stock}
                        value={record.exportQuantity || 0}
                        onChange={(value) =>
                          handleQuantityChange(record.id, value || 0)
                        }
                        size="small"
                        required
                      />
                    ),
                  },

                  {
                    title: "Action",
                    key: "action",
                    width: 2,
                    render: (_, record) => (
                      <Button
                        size="small"
                        danger
                        onClick={() => handleRemoveFromExport(record.id)}
                      >
                        Xóa
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </Card>
      </div>

      <ModalPreviewTransfer
        open={previewModalOpen}
        selectedItems={selectedItems}
        onOk={handlePreviewOk}
        onCancel={handlePreviewCancel}
        targetWarehouseId={warehouseId!}
        targetLocationId={locationId!}
      />
    </div>
  );
};

export default WarehouseTransfer;
